import React, { forwardRef } from 'react'
import { Button } from 'antd'
import { useAuth } from '../../hooks/useAuth'

/**
 * 权限包装组件 - 根据权限显示或隐藏子组件
 */
const PermissionWrapper = ({
  children,
  permission = null,
  permissions = null,
  role = null,
  requireAll = false,
  fallback = null,
  adminOnly = false
}) => {
  const { hasPermission, hasRole, isAdmin, hasAnyPermission, hasAllPermissions } = useAuth()

  // 检查管理员权限
  if (adminOnly && !isAdmin()) {
    return fallback
  }

  // 检查角色权限
  if (role && !hasRole(role)) {
    return fallback
  }

  // 检查单个权限
  if (permission && !hasPermission(permission)) {
    return fallback
  }

  // 检查多个权限
  if (permissions && Array.isArray(permissions)) {
    const hasAccess = requireAll
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions)

    if (!hasAccess) {
      return fallback
    }
  }

  return children
}

/**
 * 无权限提示组件
 */
export const NoPermission = ({ message = '您没有权限访问此功能' }) => (
  <div className="no-permission">
    <div className="no-permission-icon">🔒</div>
    <div className="no-permission-message">{message}</div>
  </div>
)

/**
 * 管理员专用组件
 */
export const AdminOnly = ({ children, fallback = <NoPermission message="此功能仅限管理员使用" /> }) => (
  <PermissionWrapper adminOnly={true} fallback={fallback}>
    {children}
  </PermissionWrapper>
)

/**
 * 基于权限的按钮组件
 */
export const PermissionButton = forwardRef(({
  children,
  permission,
  permissions,
  requireAll = false,
  adminOnly = false,
  ...buttonProps
}, ref) => {
  const { hasPermission, isAdmin, hasAnyPermission, hasAllPermissions } = useAuth()

  // 检查权限
  let hasAccess = true

  if (adminOnly && !isAdmin()) {
    hasAccess = false
  }

  if (permission && !hasPermission(permission)) {
    hasAccess = false
  }

  if (permissions && Array.isArray(permissions)) {
    hasAccess = requireAll
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions)
  }

  if (!hasAccess) {
    return null
  }

  return (
    <Button ref={ref} {...buttonProps}>
      {children}
    </Button>
  )
})

export default PermissionWrapper 